import { useCallback, useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks";
import {
  getEnvironmentsAsync,
  selectEnvironmentSelectIsOpen,
  selectEnvironments,
  selectEnvironmentsLoading,
  closeEnvSelectionModal,
  selectEnvironmentSelectIsClosable,
  openEnvCreateModal,
  openEnvSelectionModal,
} from "../../store/environments/environmentsSlice";
import {
  handleSelectEnv,
  selectOnStageEnv,
  selectUser,
} from "../../store/auth/authSlice";
import { Environment } from "../../types/environment";
import PlusIcon from "./../../assets/icons/plus.svg?react";
import ArrowDownIcon from "./../../assets/icons/arrow-down.svg?react";
import EnvironmentCreateModal from "./EnvironmentCreateModal";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  DataServicesUrl,
  InstancesUrl,
  InstancesVolumesUrl,
} from "../../utils/urls";
import { handleResetInstanceVolumes } from "../../store/instances/instancesVolumesSlice";
import {
  Alert,
  Button,
  cn,
  Flex,
  Loading,
  Modal,
  Typography,
} from "djuno-design";
import { changeIpfsNodeStatus } from "../../store/ipfs/ipfsPublicSlice";
import { changeS3NodeStatus } from "../../store/s3/s3PublicSlice";
import useLimitations from "../../hooks/useLimitations";
import { changeWorkflowNodeStatus } from "../../store/workflows/workflowsSlice";

const EnvironmentSelectModal = () => {
  const dispatch = useAppDispatch();
  const isOpen = useAppSelector(selectEnvironmentSelectIsOpen);
  const closable = useAppSelector(selectEnvironmentSelectIsClosable);
  const environments = useAppSelector(selectEnvironments);
  const activeEnv = useAppSelector(selectOnStageEnv);
  const loading = useAppSelector(selectEnvironmentsLoading);

  const { numberOfEnvironmentLimit, numberOfEnvironmentLimitChecker } =
    useLimitations({ cases: ["NumberOfEnvironment"] });

  const isValidToCreateNewEnv = useMemo(
    () => !numberOfEnvironmentLimitChecker(),
    [numberOfEnvironmentLimitChecker]
  );

  const { volumeId, instanceId, databaseId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const getRouteInfo = useCallback(() => {
    const isInstanceVolumeRoute =
      location.pathname.startsWith("/instances/volumes");
    const isInstanceRoute = location.pathname.startsWith("/instances");
    const isDatabaseRoute = location.pathname.startsWith("/database");

    return {
      volumeId,
      instanceId,
      databaseId,
      isInstanceVolumeRoute,
      isInstanceRoute,
      isDatabaseRoute,
      navigate,
    };
  }, [location, volumeId, instanceId, databaseId, navigate]);

  useEffect(() => {
    if (environments.length === 0 && isOpen) {
      dispatch(getEnvironmentsAsync());
    }
  }, [dispatch, environments.length, isOpen]);

  const handleChangeEnv = (env: Environment) => {
    const {
      volumeId,
      isInstanceVolumeRoute,
      isInstanceRoute,
      isDatabaseRoute,
      navigate,
    } = getRouteInfo();

    dispatch(handleSelectEnv({ env }));
    dispatch(closeEnvSelectionModal());

    dispatch(changeIpfsNodeStatus({ status: 2 }));
    dispatch(changeS3NodeStatus({ status: 2 }));
    dispatch(changeWorkflowNodeStatus({ status: 2 }));

    if (volumeId && isInstanceVolumeRoute) {
      navigate(InstancesVolumesUrl);
      dispatch(handleResetInstanceVolumes());
    } else if (isInstanceRoute) {
      navigate(InstancesUrl);
    } else if (isDatabaseRoute) {
      navigate(DataServicesUrl("operational"));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      closable={closable}
      onClose={() => dispatch(closeEnvSelectionModal())}
      contentClassName="max-w-lg"
      title="Select active environment"
    >
      {!loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 mt-5 gap-x-1 gap-y-2">
          {environments.map((env, envIdx) => (
            <div
              onClick={() => handleChangeEnv(env)}
              key={envIdx}
              className={cn(
                "flex gap-2 items-center w-full dark:border-dark-2  px-3 py-2.5 dark:text-slate-200 text-sm cursor-pointer rounded-lg",
                {
                  "bg-slate-100 dark:bg-black/20": env.Id === activeEnv?.Id,
                  "hover:bg-slate-50 hover:dark:bg-black/20":
                    env.Id !== activeEnv?.Id,
                }
              )}
            >
              <div>
                <div
                  className={cn("w-[16px] h-[16px] border rounded-full", {
                    "dark:bg-dark-3  dark:border-white/20":
                      env.Id !== activeEnv?.Id,
                    "bg-primary-300  border-0 ": env.Id === activeEnv?.Id,
                  })}
                />
              </div>
              <p className=" text-ellipsis whitespace-nowrap overflow-hidden">
                {env.Name}
              </p>
            </div>
          ))}
          <div
            onClick={() => {
              if (isValidToCreateNewEnv) {
                dispatch(openEnvCreateModal());
              }
            }}
            className={cn(
              "group flex gap-2 items-center w-full px-3 py-2.5 text-sm cursor-pointer rounded-lg border dark:text-slate-200 dark:border-white/20",
              {
                "!cursor-not-allowed opacity-70": !isValidToCreateNewEnv,
              }
            )}
          >
            <div>
              <PlusIcon className="w-4 h-4 group-hover:rotate-90 group-hover:scale-110 transition-transform duration-500" />
            </div>
            <p className="text-ellipsis whitespace-nowrap overflow-hidden">
              Create new
            </p>
          </div>
        </div>
      )}

      {loading && (
        <Flex justify="center" items="center" className="min-h-[200px]">
          <Loading borderSize={2} />
        </Flex>
      )}

      {!isValidToCreateNewEnv && numberOfEnvironmentLimit && (
        <Alert uiType="info" showIcon className="mt-5">
          <Typography.Text size="sm">
            {numberOfEnvironmentLimit.LimitationMessage}
          </Typography.Text>
        </Alert>
      )}

      <EnvironmentCreateModal />
    </Modal>
  );
};

const EnvironmentButton = () => {
  const dispatch = useAppDispatch();

  const authUser = useAppSelector(selectUser);
  const selectedEnv = useAppSelector(selectOnStageEnv);

  return (
    <Button
      className="w-full whitespace-nowrap !px-2 ml-1"
      // contentClassName=" !justify-start"
      uiType="icon"
      onClick={() =>
        authUser?.IsVerified ? dispatch(openEnvSelectionModal()) : {}
      }
    >
      <div className="w-full flex items-center justify-between">
        <span className="w-14 md:w-auto overflow-hidden text-ellipsis">
          {selectedEnv?.Name}
        </span>
        {authUser?.IsVerified && <ArrowDownIcon className="w-4 h-4" />}
      </div>
    </Button>
  );
};

export { EnvironmentButton };
export default EnvironmentSelectModal;
